import { Switch } from "antd";
import { get, isEmpty, set } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineInsertComment } from "react-icons/md";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  JOB_LIST,
  MEMBERSHIP_ORIGINS,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Information = ({
  values,
  setFieldValue,
  isFieldRequired,
  errors,
  initialValues,
  formProps,
  setPercent,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const INFORMATION = t(
    "SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.INFORMATION",
    {
      returnObjects: true,
    }
  );

  const DEFAULT_QUESTIONS = [
    "project.contact.fields.membershipOrigin",
    ![JOB_LIST.HOSPITAL_CIVIL_SERVICE, JOB_LIST.RETIRED].includes(
      get(initialValues, "project.contact.profession")
    )
      ? "project.contact.optMadelin"
      : null,
    "project.fields.acceptProductDocuments",
    "project.fields.acceptProductStatuses",
    "project.fields.acceptFees",
    "project.fields.acceptNotice",
    "project.fields.acceptTeletransmission",
    "project.fields.acceptAgreement",
    "project.fields.refuseProspecting",
  ];

  const booleanFields = [
    "project.contact.optMadelin",
    "project.fields.info.cssBeneficiary",
    "project.fields.acceptProductDocuments",
    "project.fields.acceptProductStatuses",
    "project.fields.acceptFees",
    "project.fields.acceptNotice",
    "project.fields.acceptAgreement",
    "project.contact.politicallyExposedPerson",
    "project.fields.refuseProspecting",
  ];

  useEffect(() => {
    booleanFields.forEach((field) => {
      if (get(initialValues, field) === undefined || get(initialValues, field) === null) {
        set(initialValues, field, true);
      }
    });
  }, []);

  const QUESTIONS = {
    "project.contact.fields.membershipOrigin": (
      <Select
        options={Object.entries(MEMBERSHIP_ORIGINS).map(([key, value]) => ({
          value,
          label: INFORMATION.MEMBERSHIP_ORIGINS[key],
        }))}
        label={INFORMATION.MEMBERSHIP_ORIGIN}
        placeholder={INFORMATION.MEMBERSHIP_ORIGIN}
        value={get(initialValues, "project.contact.fields.membershipOrigin")}
        errors={errors["project.contact.fields.membershipOrigin"]}
        touched={!isEmpty(errors["project.contact.fields.membershipOrigin"])}
        onChange={(e) => {
          set(initialValues, "project.contact.fields.membershipOrigin", e);
          setPercent(initialValues);
        }}
      />
    ),
    "project.contact.optMadelin": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.MADELIN}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          checked={get(initialValues, "project.contact.optMadelin") === true}
          onChange={(e) => {
            set(initialValues, "project.contact.optMadelin", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
    "project.fields.info.cssBeneficiary": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.CSH_BENEFICIARY}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          checked={
            get(initialValues, "project.fields.info.cssBeneficiary") === true
          }
          onChange={(e) => {
            set(initialValues, "project.fields.info.cssBeneficiary", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
    "project.fields.acceptProductDocuments": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.AMELLIS.DOCUMENT_INFOS}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          checked={
            get(initialValues, "project.fields.acceptProductDocuments") === true
          }
          onChange={(e) => {
            set(initialValues, "project.fields.acceptProductDocuments", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
    "project.fields.acceptProductStatuses": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.AMELLIS.STATUSES_AGREEMENT}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          checked={
            get(initialValues, "project.fields.acceptProductStatuses") === true
          }
          onChange={(e) => {
            set(initialValues, "project.fields.acceptProductStatuses", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
    "project.fields.acceptFees": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.AMELLIS.FEES}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          checked={get(initialValues, "project.fields.acceptFees") === true}
          onChange={(e) => {
            set(initialValues, "project.fields.acceptFees", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
    "project.fields.acceptNotice": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.AMELLIS.NOTICE}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          checked={get(initialValues, "project.fields.acceptNotice") === true}
          onChange={(e) => {
            set(initialValues, "project.fields.acceptNotice", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
    "project.fields.acceptTeletransmission": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.AMELLIS.TELETRANSMISSION}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          defaultChecked={get(
            initialValues,
            "project.fields.acceptTeletransmission"
          )}
          onChange={(e) => {
            set(initialValues, "project.fields.acceptTeletransmission", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
    "project.fields.acceptAgreement": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.AMELLIS.AGREEMENT}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          checked={
            get(initialValues, "project.fields.acceptAgreement") === true
          }
          onChange={(e) => {
            set(initialValues, "project.fields.acceptAgreement", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
    "project.fields.info.offerCDS": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.CDS_OFFER}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          checked={get(initialValues, "project.fields.info.offerCDS") === true}
          onChange={(e) => {
            set(initialValues, "project.fields.info.offerCDS", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
    "project.contact.politicallyExposedPerson": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.POLITICALLY_EXPOSED_PERSON.LABEL}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          checked={
            get(initialValues, "project.contact.politicallyExposedPerson") ===
            true
          }
          onChange={(e) => {
            set(initialValues, "project.contact.politicallyExposedPerson", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
    "project.fields.refuseProspecting": (
      <div className="d-flex justify--between">
        <span className="contact-stepper__content--radio--title">
          {INFORMATION.AMELLIS.AGREEMENT_REFUSE_PROSPECTION}
        </span>
        <Switch
          className="contact-stepper__content--radio--switch button__switch"
          checkedChildren={INFORMATION.OPTIONS.YES}
          unCheckedChildren={INFORMATION.OPTIONS.NO}
          // defaultChecked={get(
          //   initialValues,
          //   "project.fields.refuseProspecting"
          // )}
          checked={
            get(initialValues, "project.fields.refuseProspecting") ===
            true
          }
          onChange={(e) => {
            set(initialValues, "project.fields.refuseProspecting", e);
            setPercent(initialValues);
          }}
          onBlur={() => setPercent(initialValues)}
        />
      </div>
    ),
  };

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION}-section`}
    >
      <Form type="vertical" title={INFORMATION.TITLE}>
        <div className="grid--1">
          {DEFAULT_QUESTIONS.map((info) => (
            <div key={info}>{QUESTIONS[info]}</div>
          ))}
          <Input
            type="textarea"
            label={INFORMATION.COMMENT}
            placeholder={INFORMATION.COMMENT}
            icon={<MdOutlineInsertComment size={16} />}
            defaultValue={get(initialValues, "project.fields.info.comment")}
            errors={errors["project.fields.info.comment"]}
            touched={!isEmpty(errors["project.fields.info.comment"])}
            onChange={({ target: { value } }) => {
              set(initialValues, "project.fields.info.comment", value);
            }}
            autoSize={{ minRows: 2, maxRows: 10 }}
          />
        </div>
      </Form>
    </div>
  );
};

export default Information;
